import { useState } from "preact/hooks";
import { api } from "../api/client.ts";
import type { Erased, Member, SpaceOut } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import { download, useMembers } from "../lib/data.ts";
import { orgApi, spaceUrl } from "../lib/paths.ts";
import { type Resource, useAction } from "../lib/resource.ts";
import type { PageProps } from "../lib/state.ts";
import { toast } from "../lib/toast.ts";
import { Button, Link, StepActions } from "../ui/Button.tsx";
import { ConfirmButton, Dialog } from "../ui/Dialog.tsx";
import { Field, Radios, Select } from "../ui/form.tsx";
import { Async, Section } from "../ui/misc.tsx";
import { Steps } from "../ui/Steps.tsx";
import { buttonClass } from "../ui/styles.ts";
import { SettingsNav } from "./settings/nav.tsx";

type Kind = "access" | "correct" | "delete" | "account";
const STEPS = ["kind", "member", "respond"] as const;
type Step = (typeof STEPS)[number];

const KINDS: readonly Kind[] = ["access", "correct", "delete", "account"];

/** Where to look for what the space holds about someone: a full-text search for them, and their private items. */
function Lookups({ space, m }: { space: SpaceOut; m: Member }) {
  const search = m.email ?? m.name;
  return (
    <ul class="flex flex-col gap-3 text-sm">
      <li class="flex flex-wrap items-center gap-3">
        <Link class={buttonClass("secondary")} href={`${spaceUrl(space, "/items")}?q=${encodeURIComponent(search)}`}>
          {t("privacy.find")}
        </Link>
        <span class="text-zinc-600">{t("privacy.findHint", { search })}</span>
      </li>
      <li class="flex flex-wrap items-center gap-3">
        <Link class={buttonClass("secondary")} href={`${spaceUrl(space, "/items")}?private_to=${m.id}`}>
          {t("privacy.privateItems")}
        </Link>
        <span class="text-zinc-600">{t("privacy.privateItemsHint")}</span>
      </li>
    </ul>
  );
}

interface RespondProps {
  space: SpaceOut;
  kind: Kind;
  m: Member | undefined;
  onBack: () => void;
  onDone: () => void;
}

/** Access: their data to download, and where else the space may hold something about them. */
function Access({ space, m, onBack }: RespondProps & { m: Member }) {
  const [exportData, exporting] = useAction(() =>
    download(orgApi(space, `/members/${m.id}/export`), `revisa-member-${m.id}.json`),
  );
  return (
    <>
      <p class="mb-4 max-w-3xl text-sm text-zinc-700">{t("privacy.accessHint")}</p>
      <Lookups space={space} m={m} />
      <StepActions onBack={onBack}>
        <Button variant="primary" busy={exporting} onClick={() => exportData()}>
          {t("privacy.export")}
        </Button>
      </StepActions>
    </>
  );
}

/** Correction: they're fixed where they're wrong, the items themselves. */
function Correct({ space, m, onBack, onDone }: RespondProps & { m: Member }) {
  return (
    <>
      <p class="mb-4 max-w-3xl text-sm text-zinc-700">{t("privacy.correctHint")}</p>
      <Lookups space={space} m={m} />
      <StepActions onBack={onBack}>
        <Button variant="primary" onClick={onDone}>
          {t("common.done")}
        </Button>
      </StepActions>
    </>
  );
}

/** Erasure: delete permanently what must go, then erase them from the space. */
function Delete({ space, m, onBack, onDone }: RespondProps & { m: Member }) {
  const [erase, erasing] = useAction(async () => {
    const r = await api.post<Erased>(orgApi(space, `/members/${m.id}/erase`));
    toast(t("privacy.erased", { name: m.name, count: r.private_items }));
    onDone();
  });
  return (
    <>
      <ol class="flex max-w-3xl list-decimal flex-col gap-4 pl-5 text-sm text-zinc-700">
        <li>
          <p class="mb-3">{t("privacy.deleteContent")}</p>
          <Lookups space={space} m={m} />
        </li>
        <li>{t("privacy.deleteMember")}</li>
      </ol>
      <StepActions onBack={onBack}>
        <ConfirmButton
          variant="danger"
          label={t("privacy.erase")}
          title={t("privacy.eraseTitle", { name: m.name })}
          message={t("privacy.eraseMessage")}
          confirmLabel={t("privacy.erase")}
          typeToConfirm={m.name}
          busy={erasing}
          onConfirm={erase}
        />
      </StepActions>
    </>
  );
}

/** Their own account: they handle it themselves, from their profile. */
function Account({ onBack, onDone }: RespondProps) {
  return (
    <>
      <p class="max-w-3xl text-sm text-zinc-700">{t("privacy.accountHint")}</p>
      <StepActions onBack={onBack}>
        <Button variant="primary" onClick={onDone}>
          {t("common.done")}
        </Button>
      </StepActions>
    </>
  );
}

function Respond(p: RespondProps) {
  const { m } = p;
  if (p.kind === "account" || !m) {
    return <Account {...p} />;
  }
  return { access: <Access {...p} m={m} />, correct: <Correct {...p} m={m} />, delete: <Delete {...p} m={m} /> }[
    p.kind
  ];
}

/** Who the request is about. */
function PickMember(p: {
  members: Resource<Member[]>;
  picked: string;
  onPick: (id: string) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  return (
    <Async r={p.members}>
      {(list) => (
        <>
          <div class="max-w-sm">
            <Field label={t("privacy.member")}>
              <Select
                value={p.picked}
                onValue={p.onPick}
                options={[
                  { value: "", label: t("privacy.pick") },
                  ...list.map((x) => ({ value: x.id, label: x.email ? `${x.name} (${x.email})` : x.name })),
                ]}
              />
            </Field>
          </div>
          <StepActions onBack={p.onBack}>
            <Button variant="primary" disabled={p.picked === ""} onClick={p.onNext}>
              {t("common.continue")}
            </Button>
          </StepActions>
        </>
      )}
    </Async>
  );
}

/** Answering a request, a step at a time: what they asked for, who they are, the response. */
function RespondSteps({ space, onDone }: { space: SpaceOut; onDone: () => void }) {
  const members = useMembers(space);
  const [step, setStep] = useState<Step>("kind");
  const [kind, setKind] = useState<Kind>("access");
  const [picked, setPicked] = useState("");
  return (
    <>
      <Steps
        labels={[t("privacy.step.kind"), t("privacy.step.member"), t("privacy.step.respond")]}
        current={STEPS.indexOf(step)}
      />
      {step === "kind" ? (
        <>
          <Radios
            label={t("privacy.step.kind")}
            value={kind}
            onValue={setKind}
            options={KINDS.map((k) => ({
              value: k,
              label: t(`privacy.kind.${k}`),
              hint: t(`privacy.kind.${k}Hint`),
            }))}
          />
          <StepActions>
            <Button variant="primary" onClick={() => setStep(kind === "account" ? "respond" : "member")}>
              {t("common.continue")}
            </Button>
          </StepActions>
        </>
      ) : null}
      {step === "member" ? (
        <PickMember
          members={members}
          picked={picked}
          onPick={setPicked}
          onBack={() => setStep("kind")}
          onNext={() => setStep("respond")}
        />
      ) : null}
      {step === "respond" ? (
        <Respond
          space={space}
          kind={kind}
          m={members.data?.find((x) => x.id === picked)}
          onBack={() => setStep(kind === "account" ? "kind" : "member")}
          onDone={onDone}
        />
      ) : null}
    </>
  );
}

/**
 * Privacy (admins): answering a person's request about their data (GDPR, LGPD, CPRA) in three steps: what they asked
 * for, who they are, and the response, with the space's tools for each. The space decides; Revisa helps.
 */
export function Privacy({ space }: PageProps) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <SettingsNav space={space} tab="/privacy" />
      <p class="mb-2 max-w-3xl text-sm text-zinc-700">{t("privacy.intro")}</p>
      <p class="mb-4 max-w-3xl text-sm text-zinc-600">{t("privacy.deadlines")}</p>
      <Button variant="primary" onClick={() => setOpen(true)}>
        {t("privacy.respond")}
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} title={t("privacy.respond")} wide={true}>
        <RespondSteps space={space} onDone={() => setOpen(false)} />
      </Dialog>
      <div class="mt-12 grid gap-8 md:grid-cols-2">
        <Section title={t("privacy.orgTitle")}>
          <ul class="flex list-disc flex-col gap-2 pl-5 text-sm text-zinc-600">
            <li>{t("privacy.roles")}</li>
            <li>{t("privacy.cpra")}</li>
            <li>{t("privacy.contentHint")}</li>
          </ul>
        </Section>
        <Section title={t("privacy.personalTitle")}>
          <p class="mb-4 text-sm text-zinc-600">{t("privacy.ownHint")}</p>
          <Link href="/profile/privacy" class={buttonClass("secondary")}>
            {t("privacy.yourSettings")}
          </Link>
        </Section>
      </div>
    </>
  );
}
